import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Table } from '@/components_deprecated'
import { PlainLink } from '@/components_deprecated/Link'

import type { ShallowUserNode } from '@/modules/users'
import { useState } from 'react'
import styled, { css } from 'styled-components'
import { Color, SettingsColorPicker } from '../common'
import type { IssueCategoriesSettingsForm, IssueCategoryForSettings } from './types'
import React from 'react'

const ResponsibleUser = styled(PlainLink)`
  display: flex;
  align-items: center;

  span {
    margin-left: 8px;

    &:hover {
      color: ${props => props.theme.colors.secondary};
    }
  }
`

interface StaticCategoriesTableHeadProps {
  collapsed: boolean
}
const StaticCategoriesTableHead = styled.thead<StaticCategoriesTableHeadProps>`
  cursor: pointer;
  ${props =>
    !props.collapsed &&
    css`
      tr:first-of-type {
        th {
          &:first-of-type {
            border-bottom-left-radius: 0;
          }
          &:last-of-type {
            border-bottom-right-radius: 0;
          }
        }
      }
      tr:last-of-type {
        th {
          padding-top: 0;

          &:first-of-type {
            border-top-left-radius: 0;
          }
          &:last-of-type {
            border-top-right-radius: 0;
          }
        }
      }
    `};

  th span {
    margin-left: 1ch;
    text-transform: none;
    color: ${props => props.theme.colors.gray3};
    font-weight: 500;
  }
`

const SubCategoryContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  flex-direction: row;
`

const SubCategory = styled.span<{ background: string }>`
  padding: 4px 12px;
  border-radius: 40px;
  width: fit-content;
  background: ${props => props.background};
`

interface IssueCategorySettingsTableProps {
  editableCategories: IssueCategoryForSettings[]
  uneditableCategories: IssueCategoryForSettings[]

  form: IssueCategoriesSettingsForm

  temporaryEditColor: {
    [id: string]: string | null
  }

  onSelectResponsibleUser: (
    categoryId: string | null,
    currentUser: ShallowUserNode | null
  ) => void
  onUpdateColor: (categoryId: string) => (color: string) => void
  onEditClick?: (category: IssueCategoryForSettings) => void
  onDelete: (category: IssueCategoryForSettings) => void
}

export const IssueCategorySettingsTable: React.FC<
  IssueCategorySettingsTableProps
> = ({
  editableCategories,
  uneditableCategories,

  form,

  temporaryEditColor,

  onUpdateColor,
  onEditClick,
  onDelete,
}) => {
  const translations = useTranslate({
    name: 'common.name',
    responsibleUser: 'common.responsible-user',
    choose: 'common.choose-ellipsis',

    staticCategories: 'settings.static-categories',
    staticCategoriesInfo: 'settings.info.issue-categories-uneditable',
    reminder: 'common.reminder',
  })

  const [staticsCollapsed, setStaticsCollapsed] = useState(false)

  function handleEditClick(category: IssueCategoryForSettings) {
    onEditClick?.(category)
  }

  return (
    <Table
      noShadow
      noBorders
      noHeaderMargin
      noRowMargin
      rowHoverColor="secondary"
    >
      <thead>
        <tr>
          <th></th>
          <th>{translations.name}</th>
          <th>{translations.responsibleUser}</th>
          <th colSpan={3}>{translations.reminder}</th>
        </tr>
      </thead>

      <tbody>
        {editableCategories.map(category => (
          <React.Fragment key={category.id}>
            <tr>
              <td width="1px">
                <Color
                  color={temporaryEditColor[category.id] ?? category.color}
                >
                  <SettingsColorPicker
                    value={temporaryEditColor[category.id] ?? category.color}
                    onChange={onUpdateColor(category.id)}
                  />
                </Color>
              </td>

              <td>{category.name}</td>

              <td>
                <ResponsibleUser
                  to={
                    category.responsibleUser
                      ? `/users/${category.responsibleUser.id}`
                      : '#'
                  }
                >
                  <span>
                    {form.responsibleUser?.fullName ??
                      category.responsibleUser?.fullName}
                  </span>
                </ResponsibleUser>
              </td>

              <td width="1px">
                {category.automaticReminderInterval ? (
                  <Icon icon="bell" color="gray6" />
                ) : (
                  ''
                )}
              </td>

              <td width="1px">
                <Icon
                  icon="edit"
                  cursor="pointer"
                  color="gray6"
                  hoverColor="secondary"
                  onClick={() => handleEditClick(category)}
                />
              </td>

              <td width="1px">
                <Icon
                  icon="times"
                  cursor="pointer"
                  color="gray6"
                  hoverColor="red"
                  onClick={() => onDelete(category)}
                />
              </td>
            </tr>
            <tr>
              <td colSpan={6}>
                <SubCategoryContainer>
                  {category?.issueSubcategories?.edges?.map(subcategory => (
                    <SubCategory
                      key={subcategory.node.id}
                      background={
                        temporaryEditColor[category.id] ?? category.color
                      }
                    >
                      {subcategory.node.name}
                    </SubCategory>
                  ))}
                </SubCategoryContainer>
              </td>
            </tr>
          </React.Fragment>
        ))}
      </tbody>

      <StaticCategoriesTableHead
        collapsed={staticsCollapsed}
        onClick={() => setStaticsCollapsed(v => !v)}
      >
        <tr>
          <th>
            <Icon
              icon={staticsCollapsed ? 'chevron-down' : 'chevron-up'}
              color="gray5"
              hoverColor="secondary"
              cursor="pointer"
            />
          </th>

          <th colSpan={9999}>
            {translations.staticCategories}:
            <span>{translations.staticCategoriesInfo}</span>
          </th>
        </tr>

        {!staticsCollapsed && (
          <tr>
            <th></th>
            <th>{translations.name}</th>
            <th>{translations.responsibleUser}</th>
            <th colSpan={4}>{translations.reminder}</th>
          </tr>
        )}
      </StaticCategoriesTableHead>

      {!staticsCollapsed && (
        <tbody>
          {uneditableCategories.map(category => (
            <React.Fragment key={category.id}>
              <tr key={category.id}>
                <td width="1px">
                  <Color
                    color={temporaryEditColor[category.id] ?? category.color}
                    disabled
                  />
                </td>

                <td>{category.name}</td>

                <td>
                  <ResponsibleUser
                    to={
                      category.responsibleUser
                        ? `/users/${category.responsibleUser.id}`
                        : '#'
                    }
                  >
                    <span>
                      {form.responsibleUser?.fullName ??
                        category.responsibleUser?.fullName}
                    </span>
                  </ResponsibleUser>
                </td>

                <td width="1px"></td>

                <td width="1px">
                  {category.automaticReminderInterval ? (
                    <Icon icon="bell" color="gray6" />
                  ) : (
                    ''
                  )}
                </td>

                <td width="1px">
                  <Icon
                    icon="edit"
                    cursor="pointer"
                    color="gray6"
                    hoverColor="secondary"
                    onClick={() => handleEditClick(category)}
                  />
                </td>
              </tr>
              <tr>
                <td colSpan={6}>
                  <SubCategoryContainer>
                    {category?.issueSubcategories?.edges?.map(subcategory => (
                      <SubCategory
                        key={subcategory.node.id}
                        background={
                          temporaryEditColor[category.id] ?? category.color
                        }
                      >
                        {subcategory.node.name}
                      </SubCategory>
                    ))}
                  </SubCategoryContainer>
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      )}
    </Table>
  )
}
